import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../../stores/authStore';
import {
  useGetNotifications,
  useGetUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
  useDeleteNotification,
  NOTIFICATION_PAGE_SIZE,
  NOTIFICATION_QUERY_KEY,
} from '../../../api/notificationApi';
import { useNotificationSocket } from '../../../hooks/useNotificationSocket';
import { acceptBindInvite } from '../../../api/customer';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-cn';

dayjs.extend(relativeTime);
dayjs.locale('zh-cn');

export default function NotificationDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const {
    data: notificationPages,
    isLoading: loadingNotifications,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchNotifications,
  } = useGetNotifications(NOTIFICATION_PAGE_SIZE, isOpen);
  const notifications = notificationPages?.pages.flatMap(page => page.data) ?? [];
  const { data: unreadCount, refetch: refetchUnread } = useGetUnreadCount(!!user);
  // 实时通道：登录后建连，通知与未读数由服务端推送，不再轮询
  useNotificationSocket(!!user);
  const markAsReadMutation = useMarkAsRead();
  const markAllAsReadMutation = useMarkAllAsRead();
  const deleteNotificationMutation = useDeleteNotification();
  const queryClient = useQueryClient();
  // 接受客户反向绑定邀请（成功后刷新邀请通知与客户列表）
  const acceptBindInviteMutation = useMutation({
    mutationFn: (inviterUserId: string) => acceptBindInvite(inviterUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  // 处理点击外部关闭下拉框
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 标记通知为已读
  const handleMarkAsRead = (notificationId: number) => {
    markAsReadMutation.mutate(notificationId);
  };

  // 标记所有通知为已读
  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  // 删除通知
  const handleDeleteNotification = (notificationId: number) => {
    deleteNotificationMutation.mutate(notificationId);
  };

  // 刷新通知
  const handleRefresh = () => {
    refetchNotifications();
    refetchUnread();
  };

  // 格式化时间（相对时间，如“3 小时前”）
  const formatTime = (dateString: string) => {
    try {
      return dayjs(dateString).fromNow();
    } catch (error) {
      return dateString;
    }
  };

  // 获取通知图标：取值一律是后端 NOTIFICATION_TYPE 注册表里的 snake_case 值。
  // 原先并列的 camelCase 分支（orderApproval / paymentReminder / orderChange）从未被写入过——
  // 十个生产端全都用 snake 字面量落库，留着只会让人以为两种写法都合法。
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'order_approval':
        return 'ri-file-check-line';
      case 'order_backfill':
        return 'ri-history-line';
      case 'order_change':
        return 'ri-refresh-line';
      // 档期前催提交方处理：不是对方的审核邀请，是自己那张单快过期了还没人审，取警示钟而非文件勾
      case 'order_review_nudge':
        return 'ri-alarm-warning-line';
      case 'netting':
        return 'ri-exchange-dollar-line';
      case 'customer_bind_invite':
        return 'ri-user-add-line';
      case 'customer_merge':
      case 'customer_merge_audit':
        return 'ri-group-line';
      case 'customer_deactivated':
        return 'ri-user-forbid-line';
      case 'manual_application':
        return 'ri-customer-service-2-line';
      case 'identity_merged':
        return 'ri-user-shared-line';
      case 'membership_expiry':
        return 'ri-vip-crown-line';
      default:
        return 'ri-notification-3-line';
    }
  };

  // 接受客户反向绑定邀请
  const handleAcceptBindInvite = (notificationId: number, inviterUserId: string | null) => {
    if (!inviterUserId) return;
    acceptBindInviteMutation.mutate(
      inviterUserId,
      {
        onSuccess: () => {
          // 后端内部已把该通知标为已读，前端缓存已作废后自动刷新
        },
      },
    );
    // 同步先把当前通知标已读，避免未读计数滞后
    handleMarkAsRead(notificationId);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* 通知图标按钮 */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-9 h-9 flex items-center justify-center text-teal-100 hover:bg-white/10 rounded-lg transition-colors cursor-pointer relative"
      >
        <i className="ri-notification-3-line text-lg md:text-xl" />
        {unreadCount && unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs font-medium text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* 通知下拉框 */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-gray-100 py-2 z-50 shadow-lg">
          {/* 下拉框标题 */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
            <h3 className="text-sm font-medium text-gray-900">通知中心</h3>
            <div className="flex items-center gap-2">
              {unreadCount && unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-teal-600 hover:text-teal-700 transition-colors cursor-pointer"
                >
                  全部标为已读
                </button>
              )}
              <button
                onClick={handleRefresh}
                className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line text-xs" />
              </button>
            </div>
          </div>

          {/* 通知列表 */}
          <div className="max-h-80 overflow-y-auto">
            {loadingNotifications ? (
              <div className="flex items-center justify-center py-8">
                <i className="ri-loader-2-line text-gray-400 animate-spin" />
              </div>
            ) : notifications.length ? (
              notifications.map(notification => {
                const isBindInvite = notification.type === 'customer_bind_invite';
                const isNetting = notification.type === 'netting';
                return (
                  <div
                    key={notification.id}
                    className={`px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${isBindInvite ? '' : 'cursor-pointer'} ${!notification.isRead ? 'bg-teal-50' : ''}`}
                    onClick={() => {
                      // 抵消审核通知 -> 跳转审核页
                      if (isNetting && notification.relatedId && notification.relatedType === 'netting_log') {
                        if (!notification.isRead) handleMarkAsRead(notification.id);
                        setIsOpen(false);
                        navigate(`/netting-review/${notification.relatedId}`);
                        return;
                      }
                      // 订单审核/变更/补录/催审通知 -> 跳转订单详情（补录单免审核，点开只看待核算的那张旧单；
                      // 催审那条是发给提交方自己的，看的同样是这张还没人审的单）
                      const isOrderNotification =
                        (notification.type === 'order_approval' ||
                          notification.type === 'order_backfill' ||
                          notification.type === 'order_change' ||
                          notification.type === 'order_review_nudge') &&
                        notification.relatedId &&
                        notification.relatedType === 'order';
                      if (isOrderNotification) {
                        if (!notification.isRead) handleMarkAsRead(notification.id);
                        setIsOpen(false);
                        navigate('/orders', { state: { openOrderId: notification.relatedId } });
                        return;
                      }
                      // 绑定邀请不自动标读，保留操作按钮
                      if (!isBindInvite && !notification.isRead) {
                        handleMarkAsRead(notification.id);
                      }
                    }}
                  >
                    <div className="flex items-start gap-3">
                      {/* 通知图标 */}
                      <div className={`w-8 h-8 flex items-center justify-center rounded-full ${!notification.isRead ? 'bg-teal-100 text-teal-600' : 'bg-gray-100 text-gray-500'}`}>
                        <i className={getNotificationIcon(notification.type)} />
                      </div>

                      {/* 通知内容 */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 truncate">{notification.title}</h4>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{notification.content}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-400">{formatTime(notification.createdAt)}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNotification(notification.id);
                            }}
                            className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <i className="ri-delete-bin-line" />
                          </button>
                        </div>

                        {/* 客户反向绑定邀请专属操作（仅未读状态下展示） */}
                        {isBindInvite && !notification.isRead && (
                          <div className="flex items-center gap-2 mt-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAcceptBindInvite(notification.id, notification.relatedId);
                              }}
                              disabled={acceptBindInviteMutation.isPending}
                              className="px-3 py-1 text-xs font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-60 rounded-md transition-colors cursor-pointer"
                            >
                              {acceptBindInviteMutation.isPending ? '处理中...' : '加为客户'}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkAsRead(notification.id);
                              }}
                              className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors cursor-pointer"
                            >
                              忽略
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="w-12 h-12 flex items-center justify-center text-gray-300 mb-2">
                  <i className="ri-notification-off-line text-2xl" />
                </div>
                <p className="text-sm text-gray-500">暂无通知</p>
              </div>
            )}
          </div>

          {/* 加载更多：是否还有下一页由后端 pagination 判定，不再按本页条数猜测 */}
          {hasNextPage && (
            <div className="px-4 py-2 border-t border-gray-100">
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="w-full text-xs text-center text-gray-500 hover:text-teal-600 disabled:opacity-60 transition-colors cursor-pointer"
              >
                {isFetchingNextPage ? '加载中…' : '加载更多'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
